import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import FormInput from "../../components/CustomComponents/Input/formInput";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";

/**
 * Child-branch deal editor: structure read-only; price override / publish / order editable.
 * Mirrors branchEditItem.jsx for products.
 */
const BranchEditDeal = () => {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { profileData } = useSelector((state) => state.auth);
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();

  const parentCompId =
    profileData?.parentComID || profileData?.ParentComID || 0;
  const currency = profileData?.compCurrency || "GBP";
  const currencySymbol = { GBP: "£", USD: "$", EUR: "€" }[currency] || currency;

  const [loading, setLoading] = useState(false);
  const [deal, setDeal] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    isPriceOverrideActive: false,
    branchDealPriceOverride: "",
    branchPublish: true,
    branchDealOrder: "",
  });

  const load = useCallback(async () => {
    if (!dealId || !CompID) return;
    if (!parentCompId) {
      setLoadError("Parent company is not configured for this branch.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError("");
    try {
      const [dealRes, assignRes] = await Promise.all([
        axiosInstance.get(APIs.GET_DEAL_BY_ID, {
          params: { DealID: dealId, CompID },
        }),
        axiosInstance.get(
          `${APIs.GET_ASSIGNED_BRANCHES_FOR_DEAL}/${dealId}/${parentCompId}`
        ),
      ]);
      const dealData = dealRes?.data?.data;
      setDeal(dealData);
      const assigns = assignRes?.data?.data || [];
      const mine = assigns.find(
        (a) =>
          Number(a.branchCompID ?? a.BranchCompID) === Number(CompID) &&
          a.isAssigned !== false &&
          a.IsAssigned !== false
      );
      if (!mine) {
        setLoadError("This deal is not assigned to your branch.");
        setAssignment(null);
        return;
      }
      setAssignment(mine);
      setForm({
        isPriceOverrideActive:
          mine.isPriceOverrideActive ?? mine.IsPriceOverrideActive ?? false,
        branchDealPriceOverride:
          mine.branchDealPriceOverride ?? mine.BranchDealPriceOverride ?? "",
        branchPublish: mine.branchPublish ?? mine.BranchPublish ?? true,
        branchDealOrder: mine.branchDealOrder ?? mine.BranchDealOrder ?? "",
      });
    } catch (err) {
      setLoadError(err?.response?.data?.message || "Failed to load deal.");
    } finally {
      setLoading(false);
    }
  }, [dealId, CompID, parentCompId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    if (!assignment) return;
    setSaving(true);
    try {
      const price =
        form.branchDealPriceOverride === ""
          ? null
          : parseFloat(form.branchDealPriceOverride);
      await axiosInstance.put(APIs.PUT_UPDATE_BRANCH_DEAL_ASSIGNMENT, {
        ID: assignment.id ?? assignment.ID,
        MasterCompID: Number(parentCompId),
        BranchCompID: Number(CompID),
        IsPriceOverrideActive: !!form.isPriceOverrideActive,
        BranchDealPriceOverride: Number.isFinite(price) ? price : null,
        ClearBranchDealPriceOverride: !form.isPriceOverrideActive || price == null,
        BranchPublish: form.branchPublish,
        BranchDealOrder:
          form.branchDealOrder === "" ? null : parseInt(form.branchDealOrder, 10),
        ClearBranchDealOrder: form.branchDealOrder === "",
      });
      showSuccessToast("Branch deal settings saved.");
      await load();
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const muted = isDarkMode ? "#b2bec3" : "#6c757d";
  const surface = isDarkMode ? "#1e2124" : "#f8f9fa";
  const cardBg = isDarkMode ? "#2d3436" : "#ffffff";
  const masterPrice = deal?.dealPrice ?? deal?.DealPrice ?? 0;

  if (loading) {
    return (
      <div className="p-3">
        <GridSectionLoader loading minHeight="320px" />
      </div>
    );
  }

  if (loadError || !assignment) {
    return (
      <div style={{ padding: 20 }}>
        <div className="alert alert-warning">{loadError || "Unable to edit this deal."}</div>
        <button
          type="button"
          className="btn text-white"
          style={{ background: themeColor }}
          onClick={() => navigate("/deal/manageDeals")}
        >
          Back to Manage Deals
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: 20,
        minHeight: "100vh",
        backgroundColor: isDarkMode ? "#1a1d29" : "#f5f7fa",
      }}
    >
      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
        <div>
          <ol
            className="breadcrumb mb-2"
            style={{ backgroundColor: "transparent", padding: 0 }}
          >
            <li className="breadcrumb-item">
              <Link
                to="/deal/manageDeals"
                style={{ color: muted, textDecoration: "none" }}
              >
                Manage Deals
              </Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: themeColor }}>
              Branch Edit Deal
            </li>
          </ol>
          <h1
            className="mb-0"
            style={{
              fontSize: 28,
              fontWeight: 700,
              color: isDarkMode ? "#ffffff" : "#2d3748",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <i className="fa fa-store" style={{ color: themeColor }} />
            Branch Deal Settings
          </h1>
        </div>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn"
            onClick={() => navigate("/deal/manageDeals")}
            style={{
              background: isDarkMode ? "#495057" : "#6c757d",
              border: "none",
              color: "white",
              borderRadius: 10,
              padding: "10px 24px",
              fontWeight: 600,
            }}
          >
            Back
          </button>
          <button
            type="button"
            className="btn text-white"
            style={{
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              borderRadius: 10,
              padding: "10px 24px",
              fontWeight: 600,
            }}
            disabled={saving}
            onClick={handleSave}
          >
            <i className="fa fa-save me-2" />
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <div
        className="card border-0 shadow-sm mb-4"
        style={{ backgroundColor: cardBg, borderRadius: 16, overflow: "hidden" }}
      >
        <div
          className="card-header border-0"
          style={{
            backgroundColor: isDarkMode ? "#2d3436" : "#f7fafc",
            padding: "16px 24px",
            borderBottom: `2px solid ${isDarkMode ? "#404040" : "#e2e8f0"}`,
            fontWeight: 600,
          }}
        >
          <i className="fa fa-info-circle me-2" style={{ color: themeColor }} />
          Deal (read-only)
        </div>
        <div className="card-body" style={{ padding: 24 }}>
          <div
            style={{
              padding: 16,
              backgroundColor: surface,
              borderRadius: 12,
              border: `1px solid ${themeColor}20`,
            }}
          >
            <div className="mb-2" style={{ fontSize: 18, fontWeight: 600 }}>
              {deal?.dealName || deal?.DealName}
            </div>
            <div className="mb-2" style={{ color: muted }}>
              Master price:{" "}
              <strong style={{ color: isDarkMode ? "#fff" : "#2d3748" }}>
                {currencySymbol}
                {Number(masterPrice || 0).toFixed(2)}
              </strong>
            </div>
            <div className="small" style={{ color: muted }}>
              Slot structure and deal details can only be edited by the parent
              company.
            </div>
          </div>
        </div>
      </div>

      <div
        className="card border-0 shadow-sm"
        style={{ backgroundColor: cardBg, borderRadius: 16, overflow: "hidden" }}
      >
        <div
          className="card-header border-0 text-white"
          style={{
            backgroundColor: themeColor,
            padding: "15px 20px",
            fontWeight: 600,
            fontSize: 16,
          }}
        >
          <i className="fas fa-sliders-h me-2" />
          Your branch overrides
        </div>
        <div className="card-body" style={{ padding: 25 }}>
          <ToggleSwitch
            label="Enable price override"
            isChecked={form.isPriceOverrideActive}
            onToggle={(v) =>
              setForm((f) => ({ ...f, isPriceOverrideActive: v }))
            }
            themeColor={themeColor}
          />
          {form.isPriceOverrideActive && (
            <FormInput
              label={`Branch deal price (${currencySymbol})`}
              type="number"
              value={form.branchDealPriceOverride}
              onchange={(e) =>
                setForm((f) => ({
                  ...f,
                  branchDealPriceOverride: e.target.value,
                }))
              }
            />
          )}
          <ToggleSwitch
            label="Show on POS"
            isChecked={!!form.branchPublish}
            onToggle={(v) => setForm((f) => ({ ...f, branchPublish: v }))}
            themeColor={themeColor}
          />
          <FormInput
            label="Deal order (leave empty to inherit)"
            type="number"
            value={form.branchDealOrder}
            onchange={(e) =>
              setForm((f) => ({ ...f, branchDealOrder: e.target.value }))
            }
          />
        </div>
      </div>
    </div>
  );
};

export default BranchEditDeal;
